import {
  Box,
  Flex,
  HStack,
  Select,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { capitalize } from 'lodash';
import { useEffect, useState } from 'react';
import Layout from '../../../components/Layout';
import Loader from '../../../components/Loader';
import { useRouter } from 'next/router';
import TripCard from '../../../components/TripCard';
import TripLayout from '../../../components/TripLayout';
import SearchBar from '../../../components/SearchBar';
import Paginator from '../../../components/Paginator';
import { withAuth } from '../../../services/auth/AuthWrapper';
import { GET_TRIPS } from '../../../apollo/queries/GetTrips';
import { useApolloClient } from '@apollo/client';
import moment from 'moment';
import { GET_BOOKER } from '../../../apollo/queries/GetBooker';
import { GET_CLIENT } from '../../../apollo/queries/GetClient';
import useAuthInfo from '../../../hooks/useAuthInfo';

const PERIOD = ['all', 'today', 'yesterday'];

const GetPeriod = (type, period) => {
  if (type === 'upcoming' && period === 2) {
    return 'tomorrow';
  } else {
    return PERIOD[period];
  }
};

const Trips = () => {
  const [activeState, setActiveState] = useState('past');
  const [period, setPeriod] = useState('all');
  const [periodIndex, setPeriodIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('all');
  const [trips, setTrips] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [searchedValue, setSearchedValue] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const router = useRouter();
  const client = useApolloClient();

  const PAST = [
    'client_canceled',
    'driver_canceled',
    'provider_canceled',
    'completed',
    'assign_failed',
  ];

  const UPCOMING = ['pending', 'accepted', 'scheduled', 'scheduled_accepted'];

  const TODAY = moment().format('YYYY-MM-DD');
  const YESTERDAY = moment().subtract(1, 'days').format('YYYY-MM-DD');
  const TOMORROW = moment().add(1, 'days').format('YYYY-MM-DD');
  const ACTIVE_STATUS = activeState === 'past' ? PAST : UPCOMING;

  const apolloClient = useApolloClient();

  const { role: userType } = useAuthInfo();

  useEffect(() => {
    if (userType === 'booker') {
      apolloClient
        .query({
          query: GET_BOOKER,
          context: {
            clientName: 'vert',
          },
        })
        .then(({ data }) => {
          setUser({ ...data.getBooker, type: 'booker' });
        })
        .catch((e) => console.log(e));
    }
    if (userType === 'client') {
      apolloClient
        .query({
          query: GET_CLIENT,
          context: {
            clientName: 'vert',
          },
        })
        .then(({ data }) => {
          setUser(data.getClient);
        })
        .catch((e) => console.log(e));
    }
    setLoading(false);
  }, [userType]);

  useEffect(() => {
    if (router.query.type || router.query.page) {
      if (user && user.id) {
        handleTrips(
          router.query.type,
          router.query.period,
          Number(router.query.page),
          router.query.status ? router.query.status : 'all'
        );
      }
    } else {
      if (user && user.id) {
        handleTrips('past', null, 1, 'all');
      }
    }
  }, [router.query, user]);

  const handleTrips = async (
    type: string | string[],
    period: string | string[],
    page: number,
    status: string | string[]
  ) => {
    setLoading(true);

    try {
      let { data, error, loading } = await client.query({
        query: GET_TRIPS,
        context: { clientName: 'vert' },
        variables: {
          filters: {
            status: status === 'all' ? ACTIVE_STATUS : [status],
            sort: 'desc',
            order_by: 'id',
            client_id:
              userType === 'client' && user.type !== 'business'
                ? user.id
                : null,
            business_id:
              userType === 'client' && user.type === 'business'
                ? user.business_id
                : null,
            search_term: searchValue ? searchValue : null,
            schedule_date:
              period === 'today' && type === 'past'
                ? TODAY
                : period === 'yesterday' && type === 'past'
                ? YESTERDAY
                : period === 'tomorrow' && type === 'upcoming'
                ? TOMORROW
                : null,
            booker_id: userType === 'booker' ? user.id : null,
          },
          page: page === 0 ? page : page - 1,
          limit: 10,
        },
      });

      setTrips([]);

      if (!error) {
        setLoading(loading);
        setTotalPages(data.getTrips.meta.total_pages);
        setPage(data.getTrips.meta.current_page + 1);
        setTrips(data.getTrips.data);
      }
    } catch (error) {
      console.log(JSON.stringify(error, null, 2));
    }
  };

  return (
    <Layout withFooter={false} withNavBar={false} withSideNavBar={true}>
      <Box
        p={'100px'}
        pt={'130px'}
        ml={50}
        minH={'100vh'}
        backgroundColor={'gray.100'}
      >
        <Loader isLoading={isLoading} size={'xl'} />
        <TripLayout
          activeState={activeState}
          onChange={(value: string) => {
            setActiveState(value);
            setPeriodIndex(0);
            setPage(1);
            setTotalPages(0);
            setStatus('all');
            router.push(`/app/trips?page=0&type=${value}&period=all`);
          }}
        >
          <Flex direction={'column'} ml={'200px'} w={700}>
            <HStack>
              <SearchBar
                onChange={(value: string) => setSearchValue(value)}
                onComplete={() =>
                  handleTrips(
                    activeState,
                    GetPeriod(activeState, period),
                    1,
                    status
                  )
                }
              />
              <Select
                name="status"
                shadow={'xs'}
                _focus={{
                  borderColor: 'brand.900',
                }}
                backgroundColor={'white'}
                focusBorderColor="transparent"
                placeholder="Select Status"
                maxW={200}
                fontSize={14}
                value={status}
                onChange={(e) => {
                  if (e.target.value !== '') {
                    setStatus(e.target.value);
                    router.push({
                      pathname: router.pathname,
                      query: {
                        page: 0,
                        type: activeState,
                        period: period,
                        status: e.target.value,
                      },
                    });
                  }
                }}
              >
                <option id="1" value="all">
                  All
                </option>
                {activeState === 'past' ? (
                  <>
                    {' '}
                    <option id="1" value="completed">
                      Completed
                    </option>
                    <option id="2" value="client_canceled">
                      Client Canceled
                    </option>
                    <option id="3" value="driver_canceled">
                      Driver Canceled
                    </option>
                    <option id="4" value="assign_failed">
                      Assign Failed
                    </option>
                  </>
                ) : (
                  <>
                    <option id="1" value="scheduled">
                      Scheduled
                    </option>
                    <option id="2" value="scheduled_accepted">
                      Scheduled Accepted
                    </option>
                  </>
                )}
              </Select>
            </HStack>
            <Tabs
              mt={10}
              isLazy
              index={periodIndex}
              onChange={(index) => {
                setPeriod(
                  index === 0
                    ? 'all'
                    : index === 1
                    ? 'today'
                    : index === 2 && activeState === 'upcoming'
                    ? 'tomorrow'
                    : 'yesterday'
                );
                setPeriodIndex(index);
                setPage(1);
                setTotalPages(0);
                setStatus('all');
                if (index === 0) {
                  router.push({
                    pathname: router.pathname,
                    query: {
                      page: page,
                      type: activeState,
                      period: 'all',
                    },
                  });
                }
                if (index === 1) {
                  router.push({
                    pathname: router.pathname,
                    query: {
                      page: page,
                      type: activeState,
                      period: 'today',
                    },
                  });
                }
                if (index === 2) {
                  router.push({
                    pathname: router.pathname,
                    query: {
                      page: page,
                      type: activeState,
                      period:
                        activeState === 'upcoming' ? 'tomorrow' : 'yesterday',
                    },
                  });
                }
              }}
            >
              <TabList>
                <Tab color="black">All trips</Tab>
                <Tab color="black">Today</Tab>
                <Tab color="black">
                  {activeState === 'upcoming' ? 'Tomorrow' : 'Yesterday'}
                </Tab>
              </TabList>

              {/* All Trips */}
              <TabPanels>
                <TabPanel>
                  {!isLoading && (
                    <>
                      {trips.length > 0 ? (
                        <Stack spacing={3}>
                          {trips.map((item, index) => (
                            <TripCard key={index} item={item} />
                          ))}
                        </Stack>
                      ) : searchValue ? (
                        <Text textAlign={'center'} m={0} fontSize={12}>
                          We could not find any trip with ID {searchedValue}
                        </Text>
                      ) : (
                        <Text
                          textAlign={'center'}
                          m={0}
                          fontSize={12}
                          color={'gray.500'}
                        >
                          You don&apos;t have any{' '}
                          {capitalize(activeState.split('_')[0])} Trips yet
                        </Text>
                      )}
                    </>
                  )}
                </TabPanel>
                {/* Today Trips */}
                <TabPanel>
                  {!isLoading && (
                    <>
                      {trips.length > 0 ? (
                        <Stack spacing={3}>
                          {trips.map((item, index) => (
                            <TripCard key={index} item={item} />
                          ))}
                        </Stack>
                      ) : searchValue ? (
                        <Text textAlign={'center'} m={0} fontSize={12}>
                          We could not find any trip with ID {searchedValue}
                        </Text>
                      ) : (
                        <Text
                          textAlign={'center'}
                          m={0}
                          fontSize={12}
                          color={'gray.500'}
                        >
                          You don&apos;t have any{' '}
                          {capitalize(activeState.split('_')[0])} Trips yet
                        </Text>
                      )}
                    </>
                  )}
                </TabPanel>
                {/* Yesterday or Tomorrow Trips */}
                <TabPanel>
                  {!isLoading && (
                    <>
                      {trips.length > 0 ? (
                        <Stack spacing={3}>
                          {trips.map((item, index) => (
                            <TripCard key={index} item={item} />
                          ))}
                        </Stack>
                      ) : searchValue ? (
                        <Text textAlign={'center'} m={0} fontSize={12}>
                          We could not find any trip with ID {searchedValue}
                        </Text>
                      ) : (
                        <Text
                          textAlign={'center'}
                          m={0}
                          fontSize={12}
                          color={'gray.500'}
                        >
                          You don&apos;t have any{' '}
                          {capitalize(activeState.split('_')[0])} Trips yet
                        </Text>
                      )}
                    </>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
            {totalPages > 0 && (
              <Paginator
                currentPage={page}
                totalPages={totalPages}
                onChange={(newPage: number) => {
                  setPage(newPage);
                }}
              />
            )}
          </Flex>
        </TripLayout>
      </Box>
    </Layout>
  );
};

export default withAuth(Trips);
